import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// components
import Layout from "layout/index";
import { SvgIconStyle, CommonInput } from "components";
import {
  Container,
  Typography,
  Box,
  Button,
  Stack,
  Snackbar,
} from "@mui/material";
import {
  BottomButtons,
  OrderCard,
  EmailInput,
  ConfirmMobile,
  ConfirmEmail,
} from "components/unitComponent";
import { keyboardLayout } from "utils/uiData";

// translations

import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import BackIcon from "assets/icons/back.svg";

import useCart from "../store";

import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import Api from "utils/wApi/index";

// ----------------------------------------------------------------------

export default function Order() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { isValid, errors },
  } = useForm({ mode: "onChange", criteriaMode: "all" });

  const [open, setOpen] = useState(null);
  const [foreigner, setForeigner] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState("write");

  const [input, setInput] = useState("010");
  const [emailInput, setEmailInput] = useState("");
  const [emailTypeInput, setEmailTypeInput] = useState("");

  // 키오스크 영수증 약관
  // const [infoTerm, setInfoTerm] = useState("");
  // 약관 동의 체크박스
  const [termAgree, setTermAgree] = useState(false);

  const [focused, setFocused] = useState("phone");

  const [layout, setLayout] = useState("numeric");
  const [snackbarState, setSnackbarState] = useState({ open: false, text: "" }); // 토스트알림 온오프

  const keyboard = useRef();
  const numKeyboard = useRef();
  const orderSubmitInProgressRef = useRef(false);

  const { cart, total, callCart } = useCart((state) => state);

  const emailContent = () => {
    return selectedEmail === "write"
      ? `${emailInput}@${emailTypeInput}`
      : `${emailInput}@${selectedEmail}`;
  };

  const handleShift = () => {
    const newLayoutName = layout === "default" ? "shift" : "default";
    setLayout(newLayoutName);
  };

  // 키보드 입력
  const onKeyPress = (button) => {
    switch (focused) {
      case "phone":
        {
          let str = input;
          // 백스페이스 처리
          if (button === "{bksp}") {
            str = str.slice(0, -1);
          }

          // 숫자 입력
          if (/^\d$/.test(button)) {
            const onlyDigits = str.replace(/\D/g, "");

            // 숫자가 11자리 이상이면 입력 무시
            if (onlyDigits.length >= 11) return;

            // 입력 상태 반영
            str += button;
          }

          setInput(str);
          keyboard.current.setInput(str);
        }
        break;
      default:
        break;
    }
    if (button === "{shift}" || button === "{lock}") handleShift();
  };

  useEffect(() => {
    if (!keyboard.current) return;
    setLayout(foreigner ? "default" : "numeric");
  }, [foreigner]);

  useEffect(() => {
    if (!keyboard.current) return;
    keyboard.current.clearInput();
    switch (focused) {
      case "phone":
        keyboard.current.setInput(input);
        break;
      case "email":
        keyboard.current.setInput(emailInput);
        break;
      case "type":
        keyboard.current.setInput(emailTypeInput);
        break;
      default:
        break;
    }
  }, [foreigner, focused]);

  useEffect(() => {
    callCart();
  }, []);

  async function onSubmit() {
    if (orderSubmitInProgressRef.current) {
      return;
    }
    orderSubmitInProgressRef.current = true;

    const body = {
      idCartMenuTimes: cart.map((item) => item.id), //상품 id 값
      amount: total, //가격
      mobile: foreigner ? undefined : input, //모바일
      email: foreigner ? emailContent() : undefined, //이메일
    };

    try {
      const { data } = await Api.payment(body);
      navigate("/pay", { state: { orderId: data.orderId } });
    } catch (error) {
      // 실시간 품절된 경우
      setSnackbarState({ open: true, text: error.message });
      setTimeout(() => {
        setSnackbarState({ open: false, text: "" });
      }, 1000);
    } finally {
      orderSubmitInProgressRef.current = false;
    }
  }

  return (
    <Layout>
      <Container
        sx={{
          position: "relative",
          px: "0 !important",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          "&:-webkit-scrollbar": { display: "none !important" },
        }}
      >
        <Box
          sx={{
            pt: 4.5,
            px: 7,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            "&:-webkit-scrollbar": { display: "none" },
          }}
        >
          <Typography variant="h2">
            {foreigner ? "Email address" : t("input-mobile")}
          </Typography>
          <Typography variant="subtitle2" color="grey.middle" my={2}>
            {foreigner
              ? "Please enter your email address."
              : t("input-mobile-guide")}
          </Typography>
          {foreigner ? (
            // 이메일 입력
            <EmailInput
              selected={selectedEmail}
              value={emailInput}
              emailValue={emailTypeInput}
              sx={{
                mb: 0.5,
              }}
              onClick={(type) => setFocused(type)}
              onSelect={(e) =>
                setValue("emailType", "") & setSelectedEmail(e.target.value)
              }
            />
          ) : (
            // 휴대폰번호 입력
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                height: 88,
                mb: 3,
              }}
            >
              <CommonInput
                onClick={() => setFocused("phone")}
                value={(() => {
                  const digitsOnly = input.replace(/\D/g, "");
                  if (digitsOnly.length < 4) return digitsOnly;
                  if (digitsOnly.length < 8)
                    return digitsOnly.replace(/(\d{3})(\d{1,4})/, "$1-$2");
                  return digitsOnly.replace(
                    /(\d{3})(\d{4})(\d{1,4})/,
                    "$1-$2-$3"
                  );
                })()}
                sx={{
                  width: "auto",
                  height: "fit-content",
                  input: {
                    fontSize: "40px !important",
                    textAlign: "center",
                    borderBottom: "3px solid #FF006E !important",
                  },
                  fieldset: { height: "88px !important", border: 0 },
                }}
              />
            </Box>
          )}
        </Box>

        <Box
          pt={10}
          sx={{
            ".hg-button": {
              minHeight: foreigner ? "70px" : "120px",
              fontSize: foreigner ? "1rem" : "1.5rem",
            },
          }}
        >
          <Keyboard
            keyboardRef={(el) => (keyboard.current = el)}
            layoutName={layout}
            layout={{ ...keyboardLayout }}
            onKeyPress={onKeyPress}
            onChange={(value) => {
              switch (focused) {
                case "email":
                  setEmailInput(value);
                  break;
                case "type":
                  setEmailTypeInput(value);
                  break;
                default:
                  break;
              }

              // 입력시 스크롤 인풋 맨뒤로 이동
              const targetId =
                focused === "phone"
                  ? "phone-input-id"
                  : focused === "email"
                  ? "email"
                  : "emailType";
              const $el = document.getElementById(targetId);

              if ($el) {
                $el.scrollLeft = $el.scrollWidth;
              }
            }}
          />
        </Box>

        {/* bottom =============================================== */}
        <Box sx={{ width: "100%", position: "absolute", bottom: 0, left: 0 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
              justifyContent: "center",
              py: 6,
            }}
          >
            <Typography variant="body2">
              {foreigner ? "" : "In case of no Korean phone number"}
            </Typography>
            <Button
              variant="outlined"
              sx={{ px: 3, textTransform: "none", color: "black" }}
              onClick={() => {
                setForeigner(!foreigner);
                if (foreigner) {
                  setInput("010");
                  setFocused("phone");
                } else {
                  setEmailInput("");
                  setEmailTypeInput("");
                  setFocused("email");
                }
              }}
            >
              <Typography variant="subtitle1">
                {foreigner ? "Proceed with Phone" : "Proceed with Email"}
              </Typography>
            </Button>
          </Box>

          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ bgcolor: "grey.soft", p: 3 }}
          >
            <Typography variant="h3">
              {foreigner ? "Total payment amount" : t("total-pay")}
            </Typography>
            {/*  */}
            <Typography variant="h3" color="primary.main">
              {foreigner
                ? `₩ ${Number(total).toLocaleString()}`
                : t("price", { price: Number(total).toLocaleString() })}
            </Typography>
          </Stack>
          <BottomButtons
            left={{
              label: t("prev"),
              onClick: () =>
                navigate("/order", {
                  replace: true,
                  state: { history: false },
                }),
            }}
            right={{
              label: t("next"),
              onClick: () => {
                if (!foreigner && input.length < 11) {
                  setSnackbarState({
                    open: true,
                    text: t("input-phone"),
                  });

                  setTimeout(() => {
                    setSnackbarState({ open: false, text: "" });
                  }, 1000);
                  return;
                }

                setOpen("confirm");
              },
              disabled:
                !total ||
                (foreigner
                  ? !/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/.test(
                      emailContent()
                    )
                  : !input.length),
            }}
          />
        </Box>

        {/* toast */}
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={snackbarState.open}
          message={snackbarState.text}
        />
        {/* toast */}

        {/* 확인 modal====================================================== */}
        {foreigner ? (
          // 결제 전, 외국인 이메일 입력 모달
          <ConfirmEmail
            {...{ foreigner }}
            t={t}
            open={open === "confirm"}
            onClose={() => setOpen(null)}
            content={emailContent()}
            termAgree={termAgree}
            setTermAgree={setTermAgree}
            onSubmit={onSubmit}
            onCancel={() =>
              setInput("") & keyboard.current.clearInput() & setOpen(null)
            }
          />
        ) : (
          // 결제 전, 내국인 휴대폰 번호 입력 모달
          <ConfirmMobile
            t={t}
            open={open === "confirm"}
            onClose={() => setOpen(null)}
            content={input}
            termAgree={termAgree}
            setTermAgree={setTermAgree}
            onSubmit={onSubmit}
            onCancel={() =>
              setInput("010") & keyboard.current.clearInput() & setOpen(null)
            }
          />
        )}
        {/* 확인 modal====================================================== */}
      </Container>
    </Layout>
  );
}
